import React, { useCallback, useMemo } from 'react';
import { cn } from '../../poker-ui/utils/cn';

const { declarationComboKey } = require('../../shared/game/belot/armenian/declarations');

/** Звук нажатия кнопки выбора */
const HARDCORE_CHOICE_SOUND_ID = 'allin-push-chips';
const HARDCORE_CHOICE_SOUND_VOL = 0.74;

/**
 * Хардкор: один выбор перед первым ходом — объявить все найденные комбинации или не объявлять ни одной.
 *
 * @param {{
 *   open: boolean,
 *   combos: Array<{ label?: string, points?: number, cards?: number[] }>,
 *   onCommitKeys: (declaredKeys: string[]) => void,
 *   title?: string,
 *   playUi?: (soundId: string, volume?: number) => void,
 *   audioSuppressed?: boolean,
 * }} props
 */
export function BelotHardcoreDeclareCombosPanel({
  open,
  combos,
  onCommitKeys,
  title = 'Комбинации',
  playUi,
  audioSuppressed = false,
}) {
  const playChoiceFx = useCallback(() => {
    if (audioSuppressed || typeof playUi !== 'function') return;
    playUi(HARDCORE_CHOICE_SOUND_ID, HARDCORE_CHOICE_SOUND_VOL);
  }, [audioSuppressed, playUi]);

  const allKeys = useMemo(() => {
    if (!Array.isArray(combos)) return [];
    return combos.map((d) => declarationComboKey(d));
  }, [combos]);

  const summaryLine = useMemo(() => {
    if (!Array.isArray(combos) || combos.length === 0) return '';
    return combos
      .map((d) => {
        const lab = typeof d?.label === 'string' && d.label.trim() ? d.label.trim() : '—';
        const pts = Number(d?.points);
        const ptsStr = Number.isFinite(pts) ? ` +${pts}` : '';
        return `${lab}${ptsStr}`;
      })
      .join(' · ');
  }, [combos]);

  if (!open || !Array.isArray(combos) || combos.length === 0) return null;

  const commit = (keys) => {
    if (typeof onCommitKeys === 'function') onCommitKeys(keys);
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-[max(7.5rem,calc(env(safe-area-inset-bottom)+6rem))] z-[220] flex justify-center px-2 pb-1 sm:bottom-[max(8rem,calc(env(safe-area-inset-bottom)+6.5rem))]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hardcore-decl-title"
    >
      <div
        className="pointer-events-auto w-full max-w-md rounded-xl border border-amber-400/40 bg-black/20 px-2.5 py-2 shadow-[0_4px_24px_rgba(0,0,0,0.35)] backdrop-blur-[10px]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="hardcore-decl-title"
          className="text-center text-[10px] font-black uppercase tracking-wide text-amber-100/95"
        >
          {title}
        </h2>
        <p className="mt-1 line-clamp-2 text-center text-[10px] leading-tight text-slate-300/90">
          {summaryLine}
        </p>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button
            type="button"
            className={cn(
              'rounded-lg border border-white/20 bg-black/30 px-2 py-2 text-center text-[10px] font-black uppercase tracking-wide text-slate-100',
              'backdrop-blur-sm transition-colors hover:border-white/30 hover:bg-black/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/70 active:translate-y-px',
            )}
            onClick={() => {
              playChoiceFx();
              commit([]);
            }}
          >
            Не объявлять
          </button>
          <button
            type="button"
            className={cn(
              'rounded-lg border border-emerald-500/50 bg-emerald-950/40 px-2 py-2 text-center text-[10px] font-black uppercase tracking-wide text-emerald-50',
              'backdrop-blur-sm shadow-[0_0_14px_rgba(16,185,129,0.18)] transition-colors hover:border-emerald-400/75 hover:bg-emerald-950/55',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400/80 active:translate-y-px',
            )}
            onClick={() => {
              playChoiceFx();
              commit(allKeys);
            }}
          >
            Объявить
          </button>
        </div>
      </div>
    </div>
  );
}
